import type { PageEntityType } from "../../../services/ChatService";
import type { DiscoveredForm, FieldDescriptor, FieldValue, FieldValueElement, PageForm, PageForms } from "./types";

// Mirrors PageFormFormatter's caps and route charset in admin-mcp — keep the two in sync.
export const MAX_FORMS = 5;
export const MAX_FIELDS_PER_FORM = 60;
export const MAX_VALUE_CHARS = 500;
export const MAX_VALUE_ELEMENTS = 50;
export const MAX_OPTIONS = 50;
export const MAX_SNAPSHOT_BYTES = 16 * 1024;
export const MAX_ROUTE_CHARS = 200;
const ROUTE_FOREIGN_CHARACTERS = /[^A-Za-z0-9_./-]+/g;

/**
 * The pathname in the charset the server accepts for a route: each run of other characters
 * (percent-escapes, unicode) becomes "_" and the result is capped. The route only tells the model
 * where the admin is, so a lossy spelling beats the server dropping the whole snapshot over it.
 */
export function routeOf(pathname: string): string {
  const route = pathname.replace(ROUTE_FOREIGN_CHARACTERS, "_").slice(0, MAX_ROUTE_CHARS);
  return route === "" ? "/" : route;
}

export interface SnapshotContext {
  route: string;
  entityType?: PageEntityType;
  entityId?: string;
  /** Interaction order: the later the admin focused a form, the higher its rank. Forms never focused rank lowest. */
  interactionRank: (form: DiscoveredForm) => number;
}

function utf8Length(text: string): number {
  return typeof TextEncoder === "undefined" ? text.length : new TextEncoder().encode(text).length;
}

/**
 * The leading elements of a list value that fit both list caps — the element count and the summed
 * length of the elements as text. The server keeps exactly this prefix, so both sides agree on it.
 */
function fittingElements(values: FieldValueElement[]): FieldValueElement[] {
  let totalLength = 0;
  const fitting: FieldValueElement[] = [];
  for (const element of values) {
    totalLength += String(element).length;
    if (fitting.length === MAX_VALUE_ELEMENTS || totalLength > MAX_VALUE_CHARS) {
      break;
    }
    fitting.push(element);
  }
  return fitting;
}

/** Whether a value can be sent as it is; one that cannot is sent as a flagged prefix and can no longer be set. */
export function valueFitsWhole(value: FieldValue | undefined): boolean {
  if (typeof value === "string") {
    return value.length <= MAX_VALUE_CHARS;
  }
  if (Array.isArray(value)) {
    return fittingElements(value).length === value.length;
  }
  return true;
}

function valuePrefix(value: FieldValue): FieldValue {
  if (typeof value === "string") {
    return value.slice(0, MAX_VALUE_CHARS);
  }
  return Array.isArray(value) ? fittingElements(value) : value;
}

// A prefix is never sent as if it were the whole value: the model could read it as complete or
// set it back, silently cutting what the field holds.
function capField(descriptor: FieldDescriptor): FieldDescriptor {
  const capped: FieldDescriptor = { ...descriptor };
  if (capped.value !== undefined && !valueFitsWhole(capped.value)) {
    capped.value = valuePrefix(capped.value);
    capped.valueTruncated = true;
  }
  if (capped.options && capped.options.length > MAX_OPTIONS) {
    capped.options = capped.options.slice(0, MAX_OPTIONS);
    capped.optionsTruncated = true;
  }
  return capped;
}

function titleOnly(form: PageForm): PageForm {
  return { formId: form.formId, title: form.title, fields: [], truncated: true };
}

function toPageForm(form: DiscoveredForm, index: number): PageForm {
  const page: PageForm = { formId: form.formId, title: form.title, fields: [] };
  if (index >= MAX_FORMS) {
    return titleOnly(page);
  }
  page.fields = form.fields.slice(0, MAX_FIELDS_PER_FORM).map((field) => capField(field.descriptor));
  const omitted = form.fields.length - MAX_FIELDS_PER_FORM;
  if (omitted > 0) {
    page.omittedFields = omitted;
  }
  return page;
}

// Over the byte cap, forms lose their fields from the tail (the least recently used end) one at
// a time; they stay listed by title so the model knows they exist.
function fitToByteCap(forms: PageForm[], render: (forms: PageForm[]) => string): PageForm[] {
  let fitted = forms;
  while (utf8Length(render(fitted)) > MAX_SNAPSHOT_BYTES) {
    let index = fitted.length - 1;
    while (index >= 0 && fitted[index].truncated) {
      index -= 1;
    }
    if (index < 0) {
      fitted = fitted.slice(0, -1);
    } else {
      fitted = fitted.map((form, position) => (position === index ? titleOnly(form) : form));
    }
    if (fitted.length === 0) {
      break;
    }
  }
  return fitted;
}

/**
 * The `PageForms` payload for a chat turn, or undefined when the page has no form. Forms are
 * ordered by last interaction, then document order, and capped the same way the server caps them
 * so both sides see the same snapshot.
 */
export function buildSnapshot(forms: DiscoveredForm[], context: SnapshotContext): PageForms | undefined {
  if (forms.length === 0) {
    return undefined;
  }
  const ordered = forms
    .map((form, index) => ({ form, index, rank: context.interactionRank(form) }))
    .sort((left, right) => right.rank - left.rank || left.index - right.index)
    .map((entry) => entry.form);
  const base: PageForms = { route: context.route, forms: [] };
  if (context.entityType) {
    base.entityType = context.entityType;
  }
  if (context.entityId) {
    base.entityId = context.entityId;
  }
  const render = (pageForms: PageForm[]): string => JSON.stringify({ ...base, forms: pageForms });
  const fitted = fitToByteCap(ordered.map(toPageForm), render);
  return fitted.length === 0 ? undefined : { ...base, forms: fitted };
}
